import { createContext, useCallback, useContext, useState, type ReactNode } from "react";
import { Toast } from "../components";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ToastType = "success" | "error" | "info";

interface ToastItem {
  id: string;
  type: ToastType;
  message: string;
  detail?: string;
}

interface ToastContextValue {
  success(message: string): void;
  error(message: string, detail?: string): void;
  info(message: string): void;
  dismiss(id: string): void;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const ToastContext = createContext<ToastContextValue | null>(null);

let nextId = 0;

const AUTO_DISMISS_MS: Record<ToastType, number> = {
  success: 5_000,
  info: 5_000,
  error: 10_000,
};

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const show = useCallback(
    (type: ToastType, message: string, detail?: string) => {
      const id = `toast-${++nextId}`;
      setToasts((prev) => [...prev, { id, type, message, detail }]);
      setTimeout(() => dismiss(id), AUTO_DISMISS_MS[type]);
      return id;
    },
    [dismiss],
  );

  const ctx: ToastContextValue = {
    success: (msg) => show("success", msg),
    error: (msg, detail) => show("error", msg, detail),
    info: (msg) => show("info", msg),
    dismiss,
  };

  return (
    <ToastContext.Provider value={ctx}>
      {children}

      {/* Fixed-position toast stack */}
      {toasts.length > 0 && (
        <div
          style={{
            position: "fixed",
            bottom: "1.5rem",
            right: "1.5rem",
            display: "flex",
            flexDirection: "column",
            gap: "0.5rem",
            zIndex: 9999,
            maxWidth: "28rem",
          }}
        >
          {toasts.map((t) => (
            <Toast key={t.id} type={t.type} detail={t.detail} onDismiss={() => dismiss(t.id)}>
              {t.message}
            </Toast>
          ))}
        </div>
      )}
    </ToastContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within a ToastProvider");
  return ctx;
}
